// src/pages/AuthVerificationHandler.tsx
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  VStack,
  Text,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { auth } from "../configs/firebase";
import GlobalSpinner from "../components/CustomReusables/GlobalSpinner";

const AuthVerificationHandler = () => {
  const toast = useToast();
  const [params] = useSearchParams();
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");

  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [expired, setExpired] = useState(false);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!mode || !oobCode) {
      setExpired(true);
      setLoading(false);
      return;
    }

    const handleAction = async () => {
      try {
        if (mode === "verifyEmail") {
          await applyActionCode(auth, oobCode);
          setVerified(true);
        } else if (mode === "resetPassword") {
          const userEmail = await verifyPasswordResetCode(auth, oobCode);
          setEmail(userEmail);
        }
      } catch (error) {
        setExpired(true);
      } finally {
        setLoading(false);
      }
    };

    handleAction();
  }, [mode, oobCode]);

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode!, newPassword);

      // Show success toast with 10s duration
      const toastId = toast({
        title: "Password reset successful",
        description: "Redirecting to login in 5 seconds...",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Navigate after 10 seconds
      setTimeout(() => {
        toast.close(toastId);
        navigate("/login");
      }, 5000);
    } catch (error) {
      toast({
        title: "Reset failed",
        description: (error as Error).message,
        status: "error",
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <VStack h="100vh" justify="center">
        <GlobalSpinner fullPage />
        <Text>Processing...</Text>
      </VStack>
    );
  }

  if (expired) {
    return (
      <VStack h="100vh" justify="center" px={4}>
        <Heading size="md">Invalid or expired link</Heading>
        <Text>Please request a new password reset or verification email.</Text>
      </VStack>
    );
  }

  if (mode === "verifyEmail" && verified) {
    return (
      <VStack h="100vh" justify="center" px={4}>
        <Heading size="md">Email Verified</Heading>
        <Text>Your email has been successfully verified.</Text>
        <Button colorScheme="blue" onClick={() => navigate("/login")}>
          Go to Login
        </Button>
      </VStack>
    );
  }

  if (mode === "resetPassword") {
    return (
      <VStack h="100vh" justify="center" px={4}>
        <Box
          p={8}
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          w="100%"
          maxW="md"
        >
          <Heading size="md" mb={4}>
            Reset Password for {email}
          </Heading>
          <FormControl mb={4}>
            <FormLabel>New Password</FormLabel>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </FormControl>
          <FormControl mb={6}>
            <FormLabel>Confirm Password</FormLabel>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </FormControl>
          <Button colorScheme="blue" onClick={handleResetPassword} w="full">
            Reset Password
          </Button>
        </Box>
      </VStack>
    );
  }

  return null;
};

export default AuthVerificationHandler;
